import React, { ReactElement, useMemo } from 'react';
import useFeatureArticleBookmarks, {
    initialBookmarkState,
    UseBookmarks,
} from '../hooks/use-feature-article-bookmarks';
import {
    AuthenticationState,
    useAuthentication,
} from '../../../hooks/use-authentication';

export const BookmarkContext = React.createContext<
    UseBookmarks & { shouldShowBookmarkFeature: boolean }
>({
    bookmarksData: initialBookmarkState,
    loading: false,
    fetchBookmarks: () => Promise.resolve(initialBookmarkState),
    isBookmarkedFeature: () => false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onBookmarkIconClick: () => {},
    shouldShowBookmarkFeature: false,
});

BookmarkContext.displayName = 'BookmarkContext';
export const BookmarkProvider = ({
    children,
    userGuid,
    vin,
}: {
    children: React.JSX.Element;
    userGuid: string;
    vin: string | null;
}) => {
    const bookHook = useFeatureArticleBookmarks({ userGuid, vin });
    const [authenticationState] = useAuthentication();

    const shouldShowBookmarkFeature = useMemo(
        () =>
            authenticationState === AuthenticationState.Authenticated &&
            vin !== null,
        [authenticationState, bookHook.bookmarksData]
    );
    const contextValue = useMemo(
        () => ({ ...bookHook, shouldShowBookmarkFeature }),
        [bookHook, shouldShowBookmarkFeature]
    );

    return (
        <BookmarkContext.Provider value={contextValue}>
            {children}
        </BookmarkContext.Provider>
    );
};

export const withBookmarkContext = (Component: any) => {
    const contextWrapper = (props: {
        userGuid: string;
        vin: string | null;
        [x: string]: any;
    }): ReactElement<any, any> | null => {
        const { userGuid, vin } = props;
        return (
            <BookmarkProvider userGuid={userGuid} vin={vin}>
                <Component {...props} />
            </BookmarkProvider>
        );
    };
    contextWrapper.displayName = 'BookmarkContextWrapper';
    return contextWrapper;
};
